<template>
  <div class="base-tale bg-white relative sm:rounded-lg overflow-y-auto">
    <slot name="table-filters">
      <div class="flex flex-col md:flex-row items-center justify-between space-y-3 md:space-y-0 md:space-x-4">
        <!-- search input -->
        <div
          v-if="isSearchable"
          class="w-full md:w-1/2"
        >
          <form class="flex items-center">
            <label for="simple-search" class="sr-only">Search</label>
            <div class="relative w-full">
              <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <span class="i-mdi-magnify w-6 h-6 text-slate-500"></span>
              </div>
              <input
                id="simple-search"
                type="text"
                class="bg-slate-100 border border-slate-300 text-sm rounded-lg focus:ring-secondary focus:border-secondary block w-full pl-10 p-2"
                placeholder="Search"
                @input="onSearch"
              >
            </div>
          </form>
        </div>

        <!-- filter button -->
        <div
          v-if="filterItems.length > 0"
          class="w-full md:w-auto flex flex-col md:flex-row space-y-2 md:space-y-0 items-stretch md:items-center justify-end md:space-x-3 flex-shrink-0"
        >
          <div>
            <Popover
              v-slot="{ open }"
              as="div"
              class="relative w-full md:w-auto md:inline-block md:text-left"
            >
              <PopoverButton :as="BaseButton">
                <span class="flex justify-between items-center">
                  <span class="i-mdi-funnel h-4 w-4 mr-2 text-slate-500"></span>
                  <span>Filter</span>
                  <span v-if="filterValue" class="text-xs text-slate-500 ml-2">
                    ({{ filterValue.label }})
                  </span>
                  <span class="i-mdi-chevron-down -mr-1 ml-1.5 w-4 h-4"></span>
                </span>
              </PopoverButton>
              <div v-if="open">
                <PopoverPanel
                  :static="true"

                  :class="clsx(
                    'absolute w-full mt-2 z-10 md:w-48 md:min-w-max p-3 bg-white rounded-lg border shadow-sm space-y-2 text-sm',
                    isSearchable ? 'right-4' : 'left-0'
                  )"
                  as="ul"
                >
                  <h6 class="mb-3 text-sm font-medium">Filter By</h6>
                  <li
                    v-for="(item, index) in filterItems"
                    :key="`filter-item-${index}`"
                    class="flex items-center"
                  >
                    <input
                      :id="item.label"
                      v-model="filterValue"
                      type="radio"
                      :value="item"
                      class="w-4 h-4 bg-slate-100 border-slate-300 rounded focus:ring-secondary checked:bg-primary-600"
                    >
                    <label
                      :for="item.label"
                      class="ml-2 text-sm font-medium"
                    >
                      {{ item.label }}
                    </label>
                  </li>
                </PopoverPanel>
              </div>
            </Popover>
          </div>
        </div>
      </div>
    </slot>

    <!-- table -->
    <div class="flow-root">
      <div class="overflow-x-auto">
        <div class="inline-block min-w-full py-2 align-middle sm:px-4">
          <table class="min-w-full divide-y divide-slate-300">
            <thead v-if="!hideHeader">
              <tr>
                <th
                  v-for="(field) in fields"
                  :key="field.key"
                  scope="col"
                  :class="clsx(
                    'py-3.5 text-left text-sm font-semibold',
                    { 'sorted': sort.column === field.key },
                  )"
                >
                  <slot
                    :name="`${field.key}-header`"
                    :column="field"
                    :sort="sort"
                    :on-sort="onSort"
                  >
                    <div
                      :class="clsx(
                        'flex items-center px-2 py-2.5 max-w-max rounded-md',
                        field.sortable && 'hover:cursor-pointer group',
                        { 'hover:bg-slate-100': field.sortable },
                        { 'bg-secondary/80 text-primary-700': sort.column === field.key },
                      )"
                      @click="field.sortable && onSort({ key: field.key })"
                    >
                      <span>{{ field.label }}</span>
                      <span
                        v-if="field.sortable"
                        :class="clsx({
                          'i-heroicons-bars-arrow-up font-semibold': sort.column === field.key && sort.direction === 'asc',
                          'i-heroicons-bars-arrow-down font-semibold': sort.column === field.key && sort.direction === 'desc',
                          'i-heroicons-arrows-up-down': sort.column !== field.key,
                        }, 'size-5 ml-2 text-slate-500 group-hover:text-slate-950')"
                      />
                    </div>
                  </slot>
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-200" >
              <tr v-if="isLoading">
                <!-- animated circle icon spinning clockwise -->
                <td :colspan="fields.length">
                  <div class="flex flex-col items-center justify-center flex-1 px-6 py-14 sm:px-14">
                    <span class="i-heroicons-arrow-path text-slate-500 size-8 animate-spin" />
                    <p class="text-sm text-center text-slate-500 pt-4">
                      Loading...
                    </p>
                  </div>
                </td>
              </tr>
              <tr v-else-if="error">
                <!-- error state -->
                <td :colspan="fields.length">
                  <div class="flex flex-col items-center justify-center flex-1 px-6 py-14 sm:px-14">
                    <span class="i-heroicons-exclamation-triangle text-red-500 size-8" />
                    <p class="text-sm text-center text-red-600 pt-4 font-medium">
                      Error loading data
                    </p>
                    <p class="text-xs text-center text-slate-500 pt-1">
                      {{ typeof error === 'string' ? error : error?.message || 'Something went wrong' }}
                    </p>
                  </div>
                </td>
              </tr>
              <tr
                v-for="(row, index) in rows"
                v-else-if="rows.length > 0"
                :key="`table-row-${index}`"
                :class="clsx(striped && 'even:bg-slate-100', { 'cursor-pointer hover:bg-slate-100': clickable })"
                @click="onSelect(index)"
                 @mouseenter="onHoverRow(index)"
              >
                <td
                  v-for="(field, subIndex) in fields"
                  :key="subIndex"
                  :class="clsx(subIndex === 0 && 'pl-4 pr-3 font-medium sm:pl-0', 'whitespace-nowrap py-4 text-sm align-top', subIndex !== 0 && 'px-3 text-slate-500')"
                >
                  <slot
                    :name="`${field.key}-data`"
                    :column="field"
                    :row="row"
                    :index="index"
                  >
                    {{ row[field.key] }}
                  </slot>
                </td>

                <!-- add actions slot -->
                <td class="px-3 py-4 text-sm font-medium text-center whitespace-nowrap">
                  <slot name="actions" :row="row" :index="index"></slot>
                </td>
              </tr>

              <tr v-if="(!rows.length || rows.length <= 0) && !isLoading && !error">
                <td :colspan="fields.length">
                  <slot name="empty-state">
                    <EmptyState />
                  </slot>
                </td>
              </tr>
            </tbody>

            <tfoot v-if="showFooter" class="divide-y divide-slate-200">
              <tr>
                <td :colspan="fields.length" class="py-3 text-sm font-medium whitespace-nowrap">
                  <slot name="footer" :rows="rows"></slot>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- pagination -->
    <nav
      v-if="paginate && (totalPages > 1) && !isLoading && !error"
      class="flex flex-col md:flex-row justify-between items-start md:items-center space-y-3 md:space-y-0 p-4"
      aria-label="Table navigation"
    >
      <span class="text-sm font-normal text-slate-500 ">
        Showing
        <span class="font-semibold">
          {{ (page - 1) * numberPerPage + 1 }} - {{ Math.min(page * numberPerPage, totalRecords) }}
        </span>
        of
        <span class="font-semibold">{{ totalRecords }}</span>
      </span>

      <ul v-if="(totalPages > 1)" class="inline-flex items-stretch -space-x-px">
        <li>
          <a
            href="#"
            :class="clsx(
              'flex items-center justify-center h-full py-1.5 px-3 ml-0 text-slate-500  bg-white rounded-l-lg border border-slate-300 hover:bg-slate-100 hover:text-slate-950',
              page === 1 && 'cursor-not-allowed opacity-50'
            )"
            :disabled="page === 1"
            @click.prevent="page !== 1 && prevPage()"
          >
            <span class="sr-only">Back</span>
            <span class="i-mdi-chevron-left w-5 h-5"></span>
          </a>
        </li>

        <!-- disabling page number as we cannont yet link them with firestore query -->
        <!-- <li
          v-for="n in pages"
          :key="n"
          :class="clsx(n === page && 'z-10', 'relative')"
        >
          <a
            href="#"
            :class="clsx(
              'flex items-center justify-center text-sm py-2 px-3 leading-tight border font-semibold',
              n === page && 'text-primary-600 bg-primary-50 border-primary-300 hover:bg-primary-100 hover:text-primary-700',
              n !== page && 'text-slate-500 bg-white border-slate-300 hover:bg-slate-100 hover:text-slate-950'
            )"
            @click.prevent
          >{{ n }}</a>
        </li> -->

        <li>
          <a
            href="#"
            :class="clsx(
              'flex items-center justify-center h-full py-1.5 px-3 leading-tight text-slate-500 bg-white rounded-r-lg border border-slate-300 hover:bg-slate-100 hover:text-slate-950',
              page === totalPages && 'cursor-not-allowed opacity-50'
            )"
            :disabled="page === totalPages"
            @click.prevent="page !== totalPages && nextPage()"
          >
            <span class="sr-only">Next</span>
            <span class="i-mdi-chevron-right w-5 h-5"></span>
          </a>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script lang="ts" setup>
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import BaseButton from '@/components/base/BaseButton.vue'
import EmptyState from '@/components/app/EmptyState.vue'
import clsx from 'clsx'
import { PropType, ref, computed, watch } from 'vue'
import { sortBy } from 'underscore'
import { QueryFieldFilterConstraint } from 'firebase/firestore'

const props = defineProps({
  items: {
    type: Array as PropType<{ [key: string]: any }[]>,
    required: true,
  },
  fields: {
    type: Array as PropType<{ key: string; label: string; sortable?: boolean }[]>,
    required: true,
  },
  striped: {
    type: Boolean,
    default: false,
  },
  hideHeader: {
    type: Boolean,
    default: false,
  },
  clickable: {
    type: Boolean,
    default: false,
  },
  sort: {
    type: Object as PropType<{ column: string; direction: 'asc' | 'desc' }>,
    default: () => ({ column: null, direction: 'asc' }),
  },
  filterItems: {
    type: Array as PropType<{ label: string; value: QueryFieldFilterConstraint }[]>,
    default: () => [],
  },
  isSearchable: {
    type: Boolean,
    default: false,
  },
  paginate: {
    type: Boolean,
    default: false,
  },
  totalRecords: {
    type: Number,
    default: 0,
  },
  totalPages: {
    type: Number,
    default: 1,
  },
  nextPage: {
    type: Function,
    default: () => {},
  },
  prevPage: {
    type: Function,
    default: () => {},
  },
  page: {
    type: Number,
    default: 1,
  },
  numberPerPage: {
    type: Number,
    default: 25,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: [String, Error, Object],
    default: null,
  },
  showFooter: {
    type: Boolean,
    default: false,
  },
})

type FilterItem = {
  label: string;
  value: QueryFieldFilterConstraint | null;
}

const emit = defineEmits(['onSearch', 'onFilter', 'rowSelected', 'rowHovered'])

const sort = ref(props.sort)
const filterValue = ref<FilterItem | null>(null)

watch(filterValue, (value) => {
  emit('onFilter', value)
})

const rows = computed(() => {
  if (!sort.value?.column) {
    return props.items || []
  }
  const { column, direction } = sort.value
  const sortedRows = sortBy(props.items || [], column)

  return direction === 'desc' ? sortedRows.reverse() : sortedRows
})

const onSort = (column: { key: string; direction?: 'asc' | 'desc' }) => {
  if (sort.value.column === column.key) {
    sort.value.direction = sort.value.direction === 'asc' ? 'desc' : 'asc'
  } else {
    sort.value = { column: column.key, direction: column.direction || 'asc' }
  }
}

const onSearch = (event: InputEvent) => {
  emit('onSearch', (event.target as HTMLInputElement).value)
}

const onSelect = (index: number) => {
  emit('rowSelected', rows.value[index])
}

const onHoverRow = (index: number) => {
  emit('rowHovered', {
    el: ref(`table-row-${index}`),
    row: rows.value[index],
    index: index,
  })
}

// const pages = computed(() => {
//   const numPages = props.totalPages || Math.ceil(props.items.length / props.numberPerPage)

//   return Array.from({ length: numPages }, (_, i) => i + 1)
// })
</script>
